import { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@base-ui/react/dialog';
import { CaretDownIcon, FunnelSimpleIcon, XIcon } from '@phosphor-icons/react';
import { Input, cn } from '@admin-panel/ui';
import { Button as AntButton } from 'antd';

export type NumericFacetOperator = 'between' | 'eq' | 'gte' | 'lte';

export type NumericFacetFilterOption = {
  key: string;
  label: string;
  group?: string;
  placeholder?: string;
  valueType?: 'integer' | 'decimal';
};

export type NumericFacetFilterRow = {
  id: string;
  facetKey: string;
  operator: NumericFacetOperator;
  min?: string;
  max?: string;
  value?: string;
};

type NumericFacetFiltersDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: Array<NumericFacetFilterRow>;
  onApply: (value: Array<NumericFacetFilterRow>) => void;
  title?: string;
  description?: string;
};

const NUMERIC_FACET_GROUPS = [
  {
    label: 'Last 7D',
    filters: [
      'Units Sold Last 7D',
      'Online Units Sold Last 7D',
      'Online GMV Last 7D',
      'Offline Units Sold Last 7D',
      'Offline GMV Last 7D',
      'ATC Count Last 7D',
      'PDP Count Last 7D',
      'Add To Wishlist Last 7D',
      'Trial Count Last 7D',
      'Total Spend Last 7D',
    ],
  },
  {
    label: 'Last 14D',
    filters: ['Units Sold Last 14D', 'Online Units Sold Last 14D', 'Offline Units Sold Last 14D'],
  },
  {
    label: 'Last 30D',
    filters: [
      'Units Sold Last 30D',
      'Online Units Sold Last 30D',
      'Online GMV Last 30D',
      'Offline Units Sold Last 30D',
      'Offline GMV Last 30D',
      'ATC Count Last 30D',
      'PDP Count Last 30D',
      'Add To Wishlist Last 30D',
      'Trial Count Last 30D',
      'Total Spend Last 30D',
    ],
  },
  {
    label: 'Last 90D',
    filters: [
      'Online Units Sold Last 90D',
      'Online GMV Last 90D',
      'Offline Units Sold Last 90D',
      'Offline GMV Last 90D',
      'ATC Count Last 90D',
      'PDP Count Last 90D',
      'Add To Wishlist Last 90D',
      'Trial Count Last 90D',
      'Total Spend Last 90D',
    ],
  },
  {
    label: 'Lifetime',
    filters: [
      'Lifetime Units Sold',
      'Lifetime GMV',
      'Lifetime Online Units Sold',
      'Lifetime Online GMV',
      'Lifetime Offline Units Sold',
      'Lifetime Offline GMV',
      'Lifetime ATC Count',
      'Lifetime PDP Count',
      'Lifetime Trial Count',
      'Lifetime Total Spend',
    ],
  },
  {
    label: 'Decayed',
    filters: [
      'Decayed Online Net Orders',
      'Decayed Offline Net Orders',
      'Decayed ATC',
      'Decayed Wishlist',
      'Decayed PLP Clicks',
      'Decayed Impressions',
      'Decayed CTR Pct',
      'Days Since Live',
      'SCRS',
    ],
  },
] as const;

const DECIMAL_NUMERIC_FACETS = new Set([
  'Online GMV Last 7D',
  'Offline GMV Last 7D',
  'Total Spend Last 7D',
  'Online GMV Last 30D',
  'Offline GMV Last 30D',
  'Total Spend Last 30D',
  'Online GMV Last 90D',
  'Offline GMV Last 90D',
  'Total Spend Last 90D',
  'Lifetime GMV',
  'Lifetime Online GMV',
  'Lifetime Offline GMV',
  'Lifetime Total Spend',
  'Decayed CTR Pct',
  'SCRS',
]);

export const NUMERIC_FACET_FILTER_OPTIONS: Array<NumericFacetFilterOption> =
  NUMERIC_FACET_GROUPS.flatMap(group =>
    group.filters.map(filterLabel => ({
      key: filterLabel,
      label: filterLabel,
      group: group.label,
      placeholder: 'Enter a value',
      valueType: DECIMAL_NUMERIC_FACETS.has(filterLabel) ? 'decimal' : 'integer',
    })),
  );

export const NUMERIC_FACET_FILTER_KEYS = NUMERIC_FACET_FILTER_OPTIONS.map(option => option.key);

const OPERATOR_OPTIONS: Array<{
  value: NumericFacetOperator;
  label: string;
}> = [
  { value: 'between', label: 'between' },
  { value: 'eq', label: 'equals' },
  { value: 'gte', label: 'greater or equal' },
  { value: 'lte', label: 'less or equal' },
];

const createFilterId = () => `facet-${Math.random().toString(36).slice(2, 10)}`;

const getDefaultRow = (facetKey?: string): NumericFacetFilterRow => ({
  id: createFilterId(),
  facetKey: facetKey ?? '',
  operator: 'between',
  min: '',
  max: '',
  value: '',
});

const sanitizeNumericInput = (
  value: string | number,
  valueType: NumericFacetFilterOption['valueType'],
) => {
  if (value === undefined || value === null || value === '') return '';

  const normalized = String(value).replace(/[^\d.]/g, '');

  if (valueType !== 'decimal') {
    return normalized.replace(/\./g, '');
  }

  const [integerPart, ...fractionParts] = normalized.split('.');
  if (fractionParts.length === 0) return normalized;
  return `${integerPart}.${fractionParts.join('')}`;
};

const isFiniteNumberString = (value?: string | number): value is string | number => {
  if (value === undefined || value === null || value === '') return false;
  const parsed = Number(value);
  return Number.isFinite(parsed);
};

const normalizeNumberString = (value: string | number) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) ? String(parsed) : String(parsed);
};

export const serializeNumericFacetFilterRows = (rows: Array<NumericFacetFilterRow>) => {
  return rows.reduce<Record<string, Array<string>>>((acc, row) => {
    if (!row.facetKey) return acc;

    let serializedValue: string | null = null;

    if (row.operator === 'between') {
      if (!isFiniteNumberString(row.min) || !isFiniteNumberString(row.max)) return acc;
      const min = normalizeNumberString(row.min);
      const max = normalizeNumberString(row.max);
      serializedValue = `${min}-${max}`;
    }

    if (row.operator === 'eq') {
      if (!isFiniteNumberString(row.value)) return acc;
      const value = normalizeNumberString(row.value);
      serializedValue = `${value}-${value}`;
    }

    if (row.operator === 'gte') {
      if (!isFiniteNumberString(row.value)) return acc;
      const value = normalizeNumberString(row.value);
      serializedValue = `${value}-Inf`;
    }

    if (row.operator === 'lte') {
      if (!isFiniteNumberString(row.value)) return acc;
      const value = normalizeNumberString(row.value);
      serializedValue = `0-${value}`;
    }

    if (!serializedValue) return acc;

    if (!acc[row.facetKey]) {
      acc[row.facetKey] = [];
    }

    acc[row.facetKey].push(serializedValue);
    return acc;
  }, {});
};

export const isNumericFacetFilterRowComplete = (row: NumericFacetFilterRow) => {
  if (!row.facetKey) return false;
  if (row.operator === 'between') {
    return isFiniteNumberString(row.min) && isFiniteNumberString(row.max);
  }
  return isFiniteNumberString(row.value);
};

export const formatNumericFacetFilterRowLabel = (
  row: NumericFacetFilterRow,
  facetOptions: Array<NumericFacetFilterOption> = NUMERIC_FACET_FILTER_OPTIONS,
) => {
  const facetLabel =
    facetOptions.find(option => option.key === row.facetKey)?.label || row.facetKey;

  if (
    row.operator === 'between' &&
    isFiniteNumberString(row.min) &&
    isFiniteNumberString(row.max)
  ) {
    return `${facetLabel}: ${normalizeNumberString(row.min)} - ${normalizeNumberString(row.max)}`;
  }

  if (row.operator === 'eq' && isFiniteNumberString(row.value)) {
    const value = normalizeNumberString(row.value);
    return `${facetLabel}: = ${value}`;
  }

  if (row.operator === 'gte' && isFiniteNumberString(row.value)) {
    return `${facetLabel}: >= ${normalizeNumberString(row.value)}`;
  }

  if (row.operator === 'lte' && isFiniteNumberString(row.value)) {
    return `${facetLabel}: <= ${normalizeNumberString(row.value)}`;
  }

  return facetLabel;
};

export const NumericFacetFiltersDialog = ({
  open,
  onOpenChange,
  value,
  onApply,
  title = 'Numeric Filters',
  description = 'Set numeric conditions. Apply commits filters.',
}: NumericFacetFiltersDialogProps) => {
  const [draftRows, setDraftRows] = useState<Array<NumericFacetFilterRow>>([]);

  useEffect(() => {
    if (!open) return;
    const existingRowsByFacet = new Map(value.map(row => [row.facetKey, row]));
    setDraftRows(
      NUMERIC_FACET_FILTER_OPTIONS.map(option => {
        const existingRow = existingRowsByFacet.get(option.key);
        return existingRow ? { ...existingRow } : getDefaultRow(option.key);
      }),
    );
  }, [open, value]);

  const groupedOptions = useMemo(() => {
    return NUMERIC_FACET_FILTER_OPTIONS.reduce<Record<string, Array<NumericFacetFilterOption>>>(
      (acc, option) => {
        const groupKey = option.group || 'Filters';
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(option);
        return acc;
      },
      {},
    );
  }, []);

  const updateRow = (
    rowId: string,
    updater: (row: NumericFacetFilterRow) => NumericFacetFilterRow,
  ) => {
    setDraftRows(previous => previous.map(row => (row.id === rowId ? updater(row) : row)));
  };

  const handleOperatorChange = (rowId: string, operator: NumericFacetOperator) => {
    updateRow(rowId, row => ({
      ...row,
      operator,
      min: '',
      max: '',
      value: '',
    }));
  };

  const handleNumberChange = (rowId: string, field: 'min' | 'max' | 'value', nextValue: string) => {
    const option = NUMERIC_FACET_FILTER_OPTIONS.find(
      item => item.key === draftRows.find(row => row.id === rowId)?.facetKey,
    );
    const sanitizedValue = sanitizeNumericInput(nextValue, option?.valueType);

    updateRow(rowId, row => ({
      ...row,
      [field]: sanitizedValue,
    }));
  };

  const handleReset = () => {
    setDraftRows(NUMERIC_FACET_FILTER_OPTIONS.map(option => getDefaultRow(option.key)));
  };

  const handleApply = () => {
    onApply(draftRows.filter(row => isNumericFacetFilterRowComplete(row)));
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-[1400] bg-black/25 backdrop-blur-sm" />
        <Dialog.Popup className="fixed inset-0 z-[1401] grid place-items-center p-4 focus-visible:outline-none">
          <div className="flex h-[min(88vh,840px)] w-[min(96vw,1040px)] max-w-full flex-col overflow-hidden rounded-lg bg-background shadow-xl ring-1 ring-foreground/10">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="flex size-8 items-center justify-center rounded-md bg-muted text-foreground">
                  <FunnelSimpleIcon size={18} />
                </div>
                <div>
                  <Dialog.Title className="m-0 text-base font-semibold text-foreground">
                    {title}
                  </Dialog.Title>
                  <p className="m-0 mt-1 text-sm text-muted-foreground">{description}</p>
                </div>
              </div>

              <button
                aria-label="Close"
                onClick={() => onOpenChange(false)}
                className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <XIcon size={16} />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
              {Object.entries(groupedOptions).map(([groupLabel, options]) => (
                <section key={groupLabel}>
                  <div className="mb-3 px-1 text-[11px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                    {groupLabel}
                  </div>

                  <div className="overflow-hidden rounded-md bg-background">
                    {options.map((option, index) => {
                      const row =
                        draftRows.find(item => item.facetKey === option.key) ||
                        getDefaultRow(option.key);
                      const isActive = isNumericFacetFilterRowComplete(row);

                      return (
                        <div
                          key={row.id}
                          className={cn(
                            'grid gap-2 px-3 py-3 md:grid-cols-[minmax(0,240px)_minmax(0,170px)_minmax(0,1fr)_32px]',
                            index > 0 ? 'border-t border-border/70' : '',
                            isActive ? 'bg-muted/20' : '',
                          )}
                        >
                          <div className="flex h-10 items-center px-1 text-sm font-medium text-foreground">
                            {option.label}
                          </div>

                          <div className="relative">
                            <select
                              value={row.operator}
                              onChange={event =>
                                handleOperatorChange(
                                  row.id,
                                  event.target.value as NumericFacetOperator,
                                )
                              }
                              className="h-10 w-full appearance-none rounded-md border border-transparent bg-muted/55 px-3 pr-8 text-sm text-foreground outline-none transition-colors focus:border-transparent focus:bg-muted/70"
                            >
                              {OPERATOR_OPTIONS.map(operatorOption => (
                                <option key={operatorOption.value} value={operatorOption.value}>
                                  {operatorOption.label}
                                </option>
                              ))}
                            </select>
                            <CaretDownIcon
                              size={14}
                              className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground"
                            />
                          </div>

                          <div
                            className={cn(
                              'grid gap-2',
                              row.operator === 'between' ? 'md:grid-cols-2' : 'md:grid-cols-1',
                            )}
                          >
                            {row.operator === 'between' ? (
                              <>
                                <Input
                                  value={row.min}
                                  onChange={event =>
                                    handleNumberChange(row.id, 'min', event.target.value)
                                  }
                                  placeholder="Min"
                                  className="h-10 rounded-md border-transparent bg-muted/55 shadow-none focus-visible:border-transparent focus-visible:ring-0"
                                  inputMode={option.valueType === 'decimal' ? 'decimal' : 'numeric'}
                                />
                                <Input
                                  value={row.max}
                                  onChange={event =>
                                    handleNumberChange(row.id, 'max', event.target.value)
                                  }
                                  placeholder="Max"
                                  className="h-10 rounded-md border-transparent bg-muted/55 shadow-none focus-visible:border-transparent focus-visible:ring-0"
                                  inputMode={option.valueType === 'decimal' ? 'decimal' : 'numeric'}
                                />
                              </>
                            ) : (
                              <Input
                                value={row.value}
                                onChange={event =>
                                  handleNumberChange(row.id, 'value', event.target.value)
                                }
                                placeholder={option.placeholder || 'Enter a value'}
                                className="h-10 rounded-md border-transparent bg-muted/55 shadow-none focus-visible:border-transparent focus-visible:ring-0"
                                inputMode={option.valueType === 'decimal' ? 'decimal' : 'numeric'}
                              />
                            )}
                          </div>

                          <button
                            aria-label="Clear filter"
                            onClick={() =>
                              updateRow(row.id, currentRow => ({
                                ...currentRow,
                                min: '',
                                max: '',
                                value: '',
                              }))
                            }
                            className={cn(
                              'inline-flex size-8 items-center justify-center self-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
                              isActive ? '' : 'opacity-40',
                            )}
                          >
                            <XIcon size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>

            <div className="flex items-center justify-between border-t border-border px-5 py-4">
              <div className="flex items-center gap-2">
                <AntButton onClick={handleReset}>Reset</AntButton>
                <AntButton onClick={() => onOpenChange(false)}>Cancel</AntButton>
                <AntButton type="primary" onClick={handleApply}>
                  Apply filters
                </AntButton>
              </div>
            </div>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
